/**
 * BFS Search Overlay
 * Instant search functionality with AJAX results
 */

(function() {
  'use strict';

  const DEBOUNCE_DELAY = 300;
  const MIN_SEARCH_LENGTH = 1;

  let searchOverlay = null;
  let searchInput = null;
  let searchForm = null;
  let searchResults = null;
  let closeButton = null;
  let searchTriggers = [];

  let isOpen = false;
  let currentQuery = '';
  let searchTimeout = null;
  let hasResults = false;
  let abortController = null;
  let defaultStateHTML = null;
  let scrollPosition = 0;

  function init() {
    searchOverlay = document.getElementById('bfs-search-overlay');
    if (!searchOverlay) return;

    searchInput = document.getElementById('bfs-search-overlay-input');
    searchForm = searchOverlay.querySelector('.bfs-search-overlay__form');
    searchResults = document.getElementById('bfs-search-results');
    closeButton = searchOverlay.querySelector('[data-action="close-search-overlay"]');

    searchTriggers = document.querySelectorAll(
      '.bfs-navigation__search-drawer-trigger, ' +
      '.bfs-search-form input[type="search"], ' +
      '.bfs-search-form input[type="text"], ' +
      '[data-action="open-search"], ' +
      'input.searchinput, ' +
      '#searchForm input[type="text"], ' +
      '.bfs-mobile-navigation__search input, ' +
      '.bfs-search-drawer__form input'
    );

    if (!searchInput || !searchForm || !searchResults) return;

    cacheDefaultState();
    bindEvents();
    updateAriaAttributes();
  }

  /**
   * Cache the initial default state HTML so we never need to re-fetch it.
   */
  function cacheDefaultState() {
    if (searchResults && searchResults.innerHTML.trim().length > 0) {
      defaultStateHTML = searchResults.innerHTML;
    }
  }

  function bindEvents() {
    searchTriggers.forEach(function(trigger) {
      if (trigger.tagName === 'INPUT') {
        trigger.addEventListener('focus', handleSearchTrigger);
        trigger.addEventListener('click', handleSearchTrigger);
        var form = trigger.closest('form');
        if (form && (
          form.id === 'searchForm' ||
          form.classList.contains('Form') ||
          form.classList.contains('bfs-search-form') ||
          (form.id && form.id.includes('bfsSearchForm'))
        )) {
          form.addEventListener('submit', function(e) {
            e.preventDefault();
            handleSearchTrigger(e);
            return false;
          });
        }
      } else {
        trigger.addEventListener('click', handleSearchTrigger);
      }
    });

    var allSearchForms = document.querySelectorAll('form[action*="/search"], .bfs-search-form, #searchForm');
    allSearchForms.forEach(function(form) {
      if (!form.id || !form.id.includes('bfs-search-overlay')) {
        form.addEventListener('submit', function(e) {
          var overlayForm = searchOverlay ? searchOverlay.querySelector('form') : null;
          if (form !== overlayForm) {
            e.preventDefault();
            open();
            return false;
          }
        });
      }
    });

    searchInput.addEventListener('input', handleSearchInput);
    searchInput.addEventListener('keydown', handleKeyDown);
    searchForm.addEventListener('submit', handleFormSubmit);

    if (closeButton) {
      closeButton.addEventListener('click', close);
    }

    searchOverlay.addEventListener('click', function(e) {
      if (e.target === searchOverlay) {
        close();
      }
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    });

    var oldSearchModal = document.getElementById('Search');
    if (oldSearchModal) {
      oldSearchModal.style.display = 'none';
      oldSearchModal.style.visibility = 'hidden';
    }

    if (window.boostPFS) {
      var searchInputs = document.querySelectorAll('input[name="q"]');
      searchInputs.forEach(function(input) {
        if (input.id !== 'bfs-search-overlay-input') {
          var newInput = input.cloneNode(true);
          input.parentNode.replaceChild(newInput, input);
        }
      });
    }

    var boostSuggestions = document.querySelectorAll('.boost-pfs-search-suggestion, .bc-sf-search-suggestion');
    boostSuggestions.forEach(function(el) {
      el.style.display = 'none';
    });
  }

  function handleSearchTrigger(e) {
    e.preventDefault();
    open();
  }

  function handleSearchInput(e) {
    var query = e.target.value.trim();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }

    if (query.length === 0) {
      cancelInflightRequest();
      showDefaultState();
      currentQuery = '';
      return;
    }

    currentQuery = query;
    if (searchInput) {
      searchInput.setAttribute('data-current-query', query);
    }

    showLoadingIndicator();

    searchTimeout = setTimeout(function() {
      performSearch(query);
    }, DEBOUNCE_DELAY);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && currentQuery.length >= MIN_SEARCH_LENGTH) {
      if (hasResults && currentQuery === searchInput.value.trim()) {
        e.preventDefault();
        return false;
      }
      return;
    }
    if (e.key === 'Escape') {
      close();
    }
  }

  function handleFormSubmit(e) {
    var query = searchInput.value.trim();
    if (query.length < MIN_SEARCH_LENGTH) {
      e.preventDefault();
      return false;
    }
    if (currentQuery === query && hasResults) {
      e.preventDefault();
      return false;
    }
    e.preventDefault();
    performSearch(query);
    return false;
  }

  function cancelInflightRequest() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  function performSearch(query) {
    if (query.length < MIN_SEARCH_LENGTH) {
      showDefaultState();
      return;
    }

    cancelInflightRequest();
    abortController = new AbortController();

    showLoadingIndicator();

    var searchUrl = '/search?view=ajax&q=' + encodeURIComponent(query) + '&type=product';
    var signal = abortController.signal;

    fetch(searchUrl, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: signal
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Search request failed');
      return response.text();
    })
    .then(function(html) {
      if (signal.aborted) return;

      if (searchResults) {
        replaceContent(html);
        searchResults.setAttribute('data-current-query', query);

        var productsGrid = searchResults.querySelector('.bfs-search-results__products-grid');
        var noResultsMessage = searchResults.querySelector('.bfs-search-results__no-results');

        if (productsGrid) {
          var productCards = productsGrid.querySelectorAll('.bfs-product-card, .Grid__Cell');
          hasResults = productCards.length > 0;
        } else {
          hasResults = false;
        }
      }

      hideLoadingIndicator();
      initSwipers();
      initSwatchesDebounced();
    })
    .catch(function(error) {
      if (error.name === 'AbortError') return;
      hideLoadingIndicator();
      showErrorState();
    });
  }

  /**
   * Replace the results container content with a smooth crossfade.
   */
  function replaceContent(html) {
    if (!searchResults) return;

    searchResults.style.opacity = '0';
    searchResults.innerHTML = html;

    requestAnimationFrame(function() {
      searchResults.style.opacity = '1';
    });
  }

  function showDefaultState() {
    if (!searchResults) return;
    hasResults = false;

    if (defaultStateHTML) {
      replaceContent(defaultStateHTML);
      searchResults.removeAttribute('data-current-query');
      initSwipers();
      initSwatchesDebounced();
      return;
    }

    var sectionId = searchOverlay ? searchOverlay.getAttribute('data-section-id') : null;
    if (!sectionId) return;

    fetch('/?section_id=' + sectionId, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function(response) { return response.text(); })
    .then(function(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var defaultContent = doc.querySelector('#bfs-search-results');

      if (defaultContent && searchResults) {
        defaultStateHTML = defaultContent.innerHTML;
        replaceContent(defaultStateHTML);
        searchResults.removeAttribute('data-current-query');
        initSwipers();
        initSwatchesDebounced();
      }
    })
    .catch(function() {});
  }

  /**
   * Subtle loading indicator — adds a class instead of replacing content.
   */
  function showLoadingIndicator() {
    if (searchResults) {
      searchResults.classList.add('is-loading');
    }
  }

  function hideLoadingIndicator() {
    if (searchResults) {
      searchResults.classList.remove('is-loading');
    }
  }

  function showErrorState() {
    if (!searchResults) return;
    hasResults = false;
    replaceContent(
      '<div class="bfs-search-results__error">' +
      '<p>Sorry, something went wrong. Please try again.</p>' +
      '</div>'
    );
  }

  /**
   * Initialize Swipers once after a short RAF-based delay.
   */
  function initSwipers() {
    requestAnimationFrame(function() {
      setTimeout(function() {
        if (window.initSearchResultsSwiper) window.initSearchResultsSwiper();
        if (window.initDefaultSwiper) window.initDefaultSwiper();

        var swiperInstance = document.querySelector('.bfs-search-results-products');
        if (swiperInstance && swiperInstance.swiper) {
          swiperInstance.swiper.update();
        }

        var defaultSwiperElements = document.querySelectorAll('[class*="bfs-search-default-products-"]');
        defaultSwiperElements.forEach(function(el) {
          if (el.swiper) el.swiper.update();
        });
      }, 100);
    });
  }

  var swatchTimer = null;
  function initSwatchesDebounced() {
    if (swatchTimer) clearTimeout(swatchTimer);
    swatchTimer = setTimeout(function() {
      if (window.initSwatches) window.initSwatches();
    }, 300);
  }

  function open() {
    if (!searchOverlay) return;
    if (isOpen) {
      if (searchInput) searchInput.focus();
      return;
    }

    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    isOpen = true;
    searchOverlay.setAttribute('aria-hidden', 'false');
    searchOverlay.classList.add('is-open');
    document.body.classList.add('bfs-search-overlay-open');
    document.body.style.top = '-' + scrollPosition + 'px';

    var searchDrawerWrapper = document.querySelector('.bfs-search-drawer-wrapper');
    if (searchDrawerWrapper) {
      searchDrawerWrapper.classList.remove('is-open');
      searchDrawerWrapper.style.display = 'none';
      searchDrawerWrapper.style.visibility = 'hidden';
      searchDrawerWrapper.style.opacity = '0';
      searchDrawerWrapper.style.pointerEvents = 'none';
    }

    if (searchInput) {
      searchInput.focus();
      setTimeout(function() { searchInput.focus(); }, 50);
      setTimeout(function() { searchInput.focus(); }, 300);
    }

    if (!currentQuery || currentQuery.length === 0) {
      showDefaultState();
    }

    updateAriaAttributes();
  }

  function close() {
    if (!searchOverlay || !isOpen) return;

    isOpen = false;
    cancelInflightRequest();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }

    searchOverlay.setAttribute('aria-hidden', 'true');
    searchOverlay.classList.remove('is-open');
    document.body.classList.remove('bfs-search-overlay-open');
    document.body.style.top = '';
    window.scrollTo(0, scrollPosition);

    var oldSearchModal = document.getElementById('Search');
    if (oldSearchModal) {
      oldSearchModal.style.display = 'none';
      oldSearchModal.style.visibility = 'hidden';
      oldSearchModal.setAttribute('aria-hidden', 'true');
    }

    var searchDrawerWrapper = document.querySelector('.bfs-search-drawer-wrapper');
    if (searchDrawerWrapper) {
      searchDrawerWrapper.classList.remove('is-open');
      searchDrawerWrapper.style.display = 'none';
      searchDrawerWrapper.style.visibility = 'hidden';
      searchDrawerWrapper.style.opacity = '0';
      searchDrawerWrapper.style.pointerEvents = 'none';
    }

    if (searchInput) {
      searchInput.value = '';
      currentQuery = '';
      searchInput.removeAttribute('data-current-query');
    }

    if (searchResults) {
      searchResults.removeAttribute('data-current-query');
    }

    hasResults = false;
    updateAriaAttributes();
  }

  function updateAriaAttributes() {
    if (!searchOverlay) return;
    var hidden = !isOpen;
    searchOverlay.setAttribute('aria-hidden', hidden.toString());
    if (closeButton) {
      closeButton.setAttribute('aria-expanded', (!hidden).toString());
    }
  }

  function handleTrendingTagClick(e) {
    var tag = e.target;
    var searchTerm = tag.getAttribute('data-search-term');
    if (searchTerm && searchInput) {
      searchInput.value = searchTerm;
      currentQuery = searchTerm;
      performSearch(searchTerm);
    }
  }

  document.addEventListener('click', function(e) {
    if (e.target.classList.contains('bfs-search-default__trending-tag')) {
      e.preventDefault();
      handleTrendingTagClick(e);
    }
  });

  function handleCollectionLinkClick(e) {
    var link = e.target.closest('a');
    if (!link || !link.href) return;

    var isCollectionLink = link.classList.contains('bfs-search-default__collections-link') ||
                           link.classList.contains('bfs-search-results__collections-link');
    if (!isCollectionLink) return;
    if (!searchOverlay || !searchOverlay.contains(link)) return;

    var searchQuery = currentQuery;
    if (!searchQuery && searchResults) {
      searchQuery = searchResults.getAttribute('data-current-query') || '';
    }
    if (!searchQuery && searchInput) {
      searchQuery = searchInput.getAttribute('data-current-query') || searchInput.value.trim() || '';
    }

    if (searchQuery && searchQuery.trim().length > 0) {
      e.preventDefault();
      e.stopPropagation();

      var url;
      try {
        url = new URL(link.href);
      } catch (err) {
        url = new URL(link.href, window.location.origin);
      }

      url.searchParams.set('q', searchQuery.trim());
      window.location.href = url.toString();
      return false;
    }
  }

  document.addEventListener('click', function(e) {
    handleCollectionLinkClick(e);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.bfsSearch = {
    open: open,
    close: close,
    isOpen: function() { return isOpen; }
  };

})();
